import axios, { AxiosInstance, AxiosError, AxiosResponse } from "axios";
import {
  IAttribute,
  IAttributeGroup,
  IAuthor,
  ICampaign,
  ITemplateSet,
  ISampleSet,
  IUser,
  IContract,
  IImageMaterial,
  MaterialRights,
  IEditData,
  LogInfoBase,
  IManageContent,
  IMaintainer,
  consts,
  isErrors,
  IErrorResponse,
  ErrorMessageFilter,
} from "@/services/Models";
import { AuthStore } from "@/stores";

const apiService = (baseURL: string, authStore: AuthStore) => {
  const getClient = (): AxiosInstance => {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      // Authorization: `Bearer ${authStore.state.token}`,
    };

    const client = axios.create({
      baseURL: `${baseURL}`,
      withCredentials: true,
      headers: headers,
    });

    client.interceptors.response.use(
      (res) => res,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          authStore.logout();
        }
        return Promise.reject(error.response);
      }
    );
    return client;
  };
  const login = (mail: string, password: string): Promise<IUser> => {
    return getClient()
      .post("/auth/login", { mail: mail, password: password })
      .then((res) => res.data);
  };
  const logout = (): Promise<void> => {
    return getClient().post("/auth/logout");
  };
  const sendUserlog = (x: LogInfoBase): Promise<void> => {
    return getClient().post("/userlog", x);
  };
  const iam = (): Promise<IUser> => {
    return getClient()
      .get("/auth/iam")
      .then((res) => res.data);
  };
  const reset = (mail: string): Promise<void> => {
    return getClient().post(`/auth/password/reset`, { mail: mail });
  };
  const setPassword = async (
    oldPassword: string,
    newPassword: string,
    checkPassword: string
  ): Promise<void> => {
    return await getClient()
      .post("/auth/password", {
        oldPassword: oldPassword,
        newPassword: newPassword,
        checkPassword: checkPassword,
      })
      .then((res) => res.data);
  };
  const getContracts = (): Promise<IContract[]> => {
    return getClient()
      .get("/contract")
      .then((res) => Array.from(res.data).map((x) => x as IContract));
  };
  const saveContract = async (contract: IContract): Promise<IContract> => {
    return await getClient()
      .post("/contract", contract)
      .then((res) => res.data as IContract);
  };
  const deleteContract = async (id: number): Promise<void> => {
    return await getClient().delete(`/contract/${id}`);
  };
  const getAuthors = (): Promise<IAuthor[]> => {
    return getClient()
      .get("/author")
      .then((res) => Array.from(res.data).map((x) => x as IAuthor));
  };
  const getAttributes = (): Promise<IAttribute[]> => {
    return getClient()
      .get("/attribute")
      .then((res) => Array.from(res.data).map((x) => x as IAttribute));
  };
  const getAttributeGroups = (): Promise<IAttributeGroup[]> => {
    return getClient()
      .get("/attributegroup")
      .then((res) => Array.from(res.data).map((x) => x as IAttributeGroup));
  };
  const getTemplateSets = (): Promise<ITemplateSet[]> => {
    return getClient()
      .get("/template")
      .then((res) => Array.from(res.data).map((x) => x as ITemplateSet));
  };
  const getSampleSets = (): Promise<ISampleSet[]> => {
    return getClient()
      .get("/sample")
      .then((res) => Array.from(res.data).map((x) => x as ISampleSet));
  };
  const getSampleAttributes = (): Promise<IAttribute[]> => {
    return getClient()
      .get("/sample/attribute")
      .then((res) => Array.from(res.data).map((x) => x as IAttribute));
  };
  const getCampaigns = (): Promise<ICampaign[]> => {
    return getClient()
      .get("/campaign")
      .then((res) => Array.from(res.data).map((x) => x as ICampaign));
  };
  const getImageMaterials = (): Promise<IImageMaterial[]> => {
    return getClient()
      .get("/imagematerial")
      .then((res) => Array.from(res.data).map((x) => x as IImageMaterial));
  };
  const uploadUserImageMaterial = async (
    dataURI: string,
    name?: string
  ): Promise<void> => {
    const mat = {
      name: name ?? "no-name",
      image: dataURI,
      right: MaterialRights.User,
    };
    await getClient().post("/imagematerial", mat);
    return;
  };
  const deleteImageMaterial = async (id: number): Promise<void> => {
    return await getClient().delete(`/imagematerial/${id}`);
  };
  const getEditDatas = (): Promise<IEditData[]> => {
    return getClient()
      .get("/edit")
      .then((res) => Array.from(res.data).map((x) => x as IEditData));
  };
  const getEditData = (id: number): Promise<IEditData> => {
    return getClient()
      .get(`/edit/${id}`)
      .then((res) => res.data as IEditData);
  };
  const saveEditData = async (editData: IEditData): Promise<IEditData | undefined> => {
    return await getClient()
      .post("/edit", editData)
      .then((res) => res.data as IEditData)
      .catch((ex: AxiosResponse) => {
        if (ex.status == 405) {
          alert(consts.maxEditDataCountOver);
        } if (isErrors(ex.data)) {
          const errors = ex.data as IErrorResponse[];
          const messages = ErrorMessageFilter(errors, "Content");
          alert(messages.join(", "));
        } else {
          alert(consts.saveEditDataFailure);
        }
        return undefined;
      });
  };
  const deleteEditData = async (id: number): Promise<void> => {
    return await getClient().delete(`/edit/${id}`);
  };
  const getManageContent = (): Promise<IManageContent> => {
    return getClient()
      .get("/manage/contents")
      .then((res) => res.data as IManageContent);
  };
  const saveManageContent = async (
    content: IManageContent
  ): Promise<IManageContent> => {
    return await getClient()
      .post("/manage/contents", content)
      .then((res) => res.data as IManageContent);
  };
  const getMaintainers = (): Promise<IMaintainer[]> => {
    return getClient()
      .get("/manage/maintainers")
      .then((res) => Array.from(res.data).map((x) => x as IMaintainer));
  };
  const saveMaintainers = async (xs: IMaintainer[]): Promise<IMaintainer[]> => {
    return await getClient()
      .post("/manage/maintainers", xs)
      .then((res) => Array.from(res.data).map((x) => x as IMaintainer));
  };

  return {
    login,
    logout,
    sendUserlog,
    iam,
    reset,
    setPassword,
    getContracts,
    saveContract,
    deleteContract,
    getAuthors,
    getAttributes,
    getAttributeGroups,
    getTemplateSets,
    getSampleSets,
    getSampleAttributes,
    getCampaigns,
    //getQuestionnaires,
    getImageMaterials,
    uploadUserImageMaterial,
    deleteImageMaterial,
    getEditDatas,
    getEditData,
    saveEditData,
    deleteEditData,
    getManageContent,
    saveManageContent,
    getMaintainers,
    saveMaintainers,
  };
};
export default apiService;
